/**
 * Kaksos Portal - My Kaksos Page (Loop 1 step four, cut 6 (b))
 *
 * The signed-in person as a MEMBER of other people's Kaksos. Lists every Kaksos
 * whose circle they belong to (GET /api/member/kaksos, JWT-gated) and, for each
 * one the owner has switched on, offers one button into the chat. Tapping it
 * collects a member token in the background (POST .../session-claim, proven by
 * the sign-in, never by a link), stores it on this phone under the Kaksos id
 * (Ruling A), and opens the chat at the member's circle.
 *
 * WISDOM 19/22: the session-claim reply is never held in page state. The token is
 * passed straight into setMemberToken and not referenced again; nothing here
 * logs. A 403 means the owner switched this Kaksos off under us: reload the list.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { memberApi, MemberKaksosRow, setMemberToken } from '../lib/api';
import DashboardLayout from '../components/DashboardLayout';

const CIRCLE_NAMES: Record<string, string> = {
    center: 'centre',
    inner: 'inner circle',
    mid: 'mid circle',
    outer: 'outer circle',
    network: 'network',
};

function circleName(level: string): string {
    return CIRCLE_NAMES[level] || level;
}

export default function MyKaksosPage() {
    const navigate = useNavigate();
    const [rows, setRows] = useState<MemberKaksosRow[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [openingId, setOpeningId] = useState<string | null>(null);

    const loadRows = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await memberApi.listKaksos();
            setRows(response.kaksos);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load your Kaksos');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    // The button: collect the key in the background, store it once, open the chat.
    async function handleOpen(row: MemberKaksosRow) {
        if (openingId) return;
        setOpeningId(row.boby_place_id);
        setError(null);
        try {
            const reply = await memberApi.sessionClaim(row.boby_place_id);
            setMemberToken(reply.boby_place_id, {
                memberToken: reply.member_token,
                kaksosId: reply.boby_place_id,
                circleLevel: reply.circle_level,
                expiresAt: reply.token_expires_at,
                ...(reply.member_name ? { memberName: reply.member_name } : {}),
            });
            const telepathcode = reply.telepathcode || row.telepathcode;
            if (!telepathcode) {
                setError('This Kaksos has no chat address yet.');
                return;
            }
            navigate(`/chat/${telepathcode}`);
        } catch (err) {
            if (err instanceof Error && err.message.startsWith('403')) {
                // The owner switched this Kaksos off; the list is the truth.
                await loadRows();
            } else {
                setError(err instanceof Error ? err.message : 'Could not open the chat');
            }
        } finally {
            setOpeningId(null);
        }
    }

    return (
        <DashboardLayout>
            <div className="max-w-3xl mx-auto px-4 py-6">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-800">My Kaksos</h1>
                    <p className="text-gray-600 mt-1">The Kaksos whose circles you are in.</p>
                </div>

                {/* Error State */}
                {error && (
                    <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6">
                        {error}
                    </div>
                )}

                {/* Loading State */}
                {isLoading ? (
                    <div className="flex justify-center py-12">
                        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : rows.length === 0 ? (
                    <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-600">
                        You are not in anyone's circle yet.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {rows.map(row => {
                            const name = row.kaksos_name || row.telepathcode || 'Kaksos';
                            const canOpen = row.switch_on && !!row.telepathcode;
                            return (
                                <div
                                    key={row.boby_place_id}
                                    className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all"
                                >
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="font-medium text-gray-800">{name}</span>
                                        <span className="px-2 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-700">
                                            {circleName(row.circle_level)}
                                        </span>
                                    </div>
                                    {row.owner_first_name && (
                                        <p className="text-sm text-gray-500 mb-3">{row.owner_first_name}'s Kaksos</p>
                                    )}
                                    {canOpen ? (
                                        <button
                                            type="button"
                                            onClick={() => handleOpen(row)}
                                            disabled={openingId !== null}
                                            className="w-full py-3 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 transition-colors disabled:opacity-50"
                                        >
                                            {openingId === row.boby_place_id ? 'Opening...' : (row.owner_first_name ? `Personal assistant of ${row.owner_first_name}` : 'Personal assistant')}
                                        </button>
                                    ) : (
                                        <p className="text-sm text-gray-500">Not connected</p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
}
